import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { GitBranch, RotateCcw } from "lucide-react";
import recruitingAPI from "../../services/recruitingApi";

type EntityType = "job" | "candidate";

interface StatusConfig {
  label: string;
  color: string;
  description: string;
}

interface TransitionRulesData {
  defaults: Record<string, string[]>;
  disabled: Record<string, string[]>;
  effective: Record<string, string[]>;
  statusConfig: Record<string, StatusConfig>;
  isUsingDefaults: boolean;
}

/**
 * TransitionRulesTab - Admin tab for managing status transition rules
 *
 * Shows a matrix of all statuses with checkboxes for each allowed transition.
 * Admins can disable default transitions and reset to defaults.
 */
const TransitionRulesTab: React.FC = () => {
  const { t } = useTranslation(["admin", "common"]);
  const [entityType, setEntityType] = useState<EntityType>("job");
  const [rulesData, setRulesData] = useState<TransitionRulesData | null>(null);
  const [disabled, setDisabled] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchRules = useCallback(
    async (type: EntityType) => {
      setLoading(true);
      try {
        const response = await recruitingAPI.getTransitionRules(type);
        const data = response.data as TransitionRulesData;
        setRulesData(data);
        setDisabled(data.disabled || {});
        setHasChanges(false);
      } catch (error) {
        console.error("Error fetching transition rules:", error);
        alert(t("transitions.messages.loadError"));
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    fetchRules(entityType);
  }, [entityType, fetchRules]);

  const handleEntityTypeChange = (type: EntityType) => {
    if (hasChanges) {
      if (!window.confirm(t("transitions.switchConfirm"))) {
        return;
      }
    }
    setEntityType(type);
  };

  const handleToggleTransition = (fromStatus: string, toStatus: string) => {
    setDisabled((prev) => {
      const updated = { ...prev };
      const currentDisabled = updated[fromStatus] || [];

      if (currentDisabled.includes(toStatus)) {
        // Re-enable: remove from disabled list
        updated[fromStatus] = currentDisabled.filter((s) => s !== toStatus);
        if (updated[fromStatus].length === 0) {
          delete updated[fromStatus];
        }
      } else {
        // Disable: add to disabled list
        updated[fromStatus] = [...currentDisabled, toStatus];
      }

      return updated;
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await recruitingAPI.updateTransitionRules(entityType, disabled);
      await fetchRules(entityType);
      alert(t("transitions.messages.saveSuccess"));
    } catch (error) {
      const err = error as {
        response?: { data?: { error?: string; message?: string } };
        message: string;
      };
      alert(
        "Error saving: " +
          (err.response?.data?.error ||
            err.response?.data?.message ||
            err.message),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(t("transitions.resetConfirm"))) {
      return;
    }
    setSaving(true);
    try {
      await recruitingAPI.resetTransitionRules(entityType);
      await fetchRules(entityType);
      alert(t("transitions.messages.resetSuccess"));
    } catch (error) {
      const err = error as {
        response?: { data?: { error?: string; message?: string } };
        message: string;
      };
      alert(
        "Error resetting: " +
          (err.response?.data?.error ||
            err.response?.data?.message ||
            err.message),
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">{t("transitions.loading")}</div>;
  }

  if (!rulesData) {
    return <div className="admin-section">{t("transitions.error")}</div>;
  }

  const { defaults, statusConfig } = rulesData;
  const statuses = Object.keys(defaults);
  const allTargetStatuses = statuses; // All statuses are potential targets

  const isDisabled = (from: string, to: string): boolean => {
    return (disabled[from] || []).includes(to);
  };

  const isDefaultTransition = (from: string, to: string): boolean => {
    return (defaults[from] || []).includes(to);
  };

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>
          <GitBranch size={16} /> {t("transitions.title")}
        </h2>
      </div>

      {/* Entity type toggle */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "20px" }}>
        <button
          className={`tab-button ${entityType === "job" ? "active" : ""}`}
          onClick={() => handleEntityTypeChange("job")}
          disabled={saving}
        >
          {t("transitions.tabs.job")}
        </button>
        <button
          className={`tab-button ${entityType === "candidate" ? "active" : ""}`}
          onClick={() => handleEntityTypeChange("candidate")}
          disabled={saving}
        >
          {t("transitions.tabs.candidate")}
        </button>
      </div>

      {/* Info text */}
      <p style={{ color: "#6b7280", fontSize: "13px", marginBottom: "16px" }}>
        {t("transitions.description")}
        {!rulesData.isUsingDefaults && (
          <span
            style={{ color: "#f59e0b", marginLeft: "8px", fontWeight: 500 }}
          >
            {t("transitions.customActive")}
          </span>
        )}
      </p>

      {/* Transition Matrix */}
      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            borderCollapse: "collapse",
            width: "100%",
            fontSize: "13px",
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  padding: "8px 12px",
                  textAlign: "left",
                  borderBottom: "2px solid #e5e7eb",
                  backgroundColor: "#f9fafb",
                  position: "sticky",
                  left: 0,
                  zIndex: 1,
                  minWidth: "160px",
                }}
              >
                {t("transitions.table.fromTo")}
              </th>
              {allTargetStatuses.map((to) => (
                <th
                  key={to}
                  style={{
                    padding: "8px 6px",
                    textAlign: "center",
                    borderBottom: "2px solid #e5e7eb",
                    backgroundColor: "#f9fafb",
                    whiteSpace: "nowrap",
                    fontSize: "12px",
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      padding: "2px 8px",
                      borderRadius: "10px",
                      backgroundColor: statusConfig[to]?.color || "#6b7280",
                      color: "#fff",
                      fontSize: "11px",
                    }}
                  >
                    {statusConfig[to]?.label || to}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {statuses.map((from) => (
              <tr key={from}>
                <td
                  style={{
                    padding: "8px 12px",
                    borderBottom: "1px solid #e5e7eb",
                    fontWeight: 500,
                    backgroundColor: "#fff",
                    position: "sticky",
                    left: 0,
                    zIndex: 1,
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      padding: "2px 8px",
                      borderRadius: "10px",
                      backgroundColor: statusConfig[from]?.color || "#6b7280",
                      color: "#fff",
                      fontSize: "11px",
                    }}
                  >
                    {statusConfig[from]?.label || from}
                  </span>
                </td>
                {allTargetStatuses.map((to) => {
                  const isSelf = from === to;
                  const isDefault = isDefaultTransition(from, to);
                  const isCurrentlyDisabled = isDisabled(from, to);

                  if (isSelf) {
                    return (
                      <td
                        key={to}
                        style={{
                          padding: "8px 6px",
                          textAlign: "center",
                          borderBottom: "1px solid #e5e7eb",
                          backgroundColor: "#f3f4f6",
                        }}
                      >
                        &mdash;
                      </td>
                    );
                  }

                  if (!isDefault) {
                    return (
                      <td
                        key={to}
                        style={{
                          padding: "8px 6px",
                          textAlign: "center",
                          borderBottom: "1px solid #e5e7eb",
                          backgroundColor: "#f9fafb",
                        }}
                      />
                    );
                  }

                  return (
                    <td
                      key={to}
                      style={{
                        padding: "8px 6px",
                        textAlign: "center",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!isCurrentlyDisabled}
                        onChange={() => handleToggleTransition(from, to)}
                        disabled={saving}
                        title={`${statusConfig[from]?.label || from} → ${statusConfig[to]?.label || to}`}
                        style={{
                          cursor: saving ? "not-allowed" : "pointer",
                          width: "16px",
                          height: "16px",
                        }}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Action buttons */}
      <div
        style={{
          display: "flex",
          gap: "12px",
          marginTop: "20px",
          alignItems: "center",
        }}
      >
        <button
          className="tab-button active"
          onClick={handleSave}
          disabled={saving || !hasChanges}
          style={{ opacity: saving || !hasChanges ? 0.6 : 1 }}
        >
          {saving
            ? t("common:actions.saving")
            : t("transitions.actions.saveChanges")}
        </button>
        <button
          className="tab-button"
          onClick={handleReset}
          disabled={saving}
          style={{ display: "flex", alignItems: "center", gap: "4px" }}
        >
          <RotateCcw size={14} /> {t("transitions.actions.resetToDefaults")}
        </button>
        {hasChanges && (
          <span style={{ color: "#f59e0b", fontSize: "13px" }}>
            {t("transitions.unsavedChanges")}
          </span>
        )}
      </div>
    </div>
  );
};

export default TransitionRulesTab;
